import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import {
    Target,
    Kanban,
    TrendingUp,
    AlertTriangle,
    CheckCircle2,
    ArrowRight,
    Plus,
    Loader2,
    X,
    Activity,
    User,
    Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { usePageTitle } from '@/hooks/usePageTitle';

export default function DashboardPage() {
    const { t } = useTranslation();
    usePageTitle('nav.dashboard');
    const { workspace } = useAuthStore();
    const [showCreateBoard, setShowCreateBoard] = useState(false);

    const { data: okrData, isLoading: okrLoading } = useQuery({
        queryKey: ['objectives', workspace?.id],
        queryFn: () => api.getObjectives(workspace!.id),
        enabled: !!workspace?.id,
    });

    const { data: boardsData, isLoading: boardsLoading } = useQuery({
        queryKey: ['boards', workspace?.id],
        queryFn: () => api.getBoards(workspace!.id),
        enabled: !!workspace?.id,
    });

    const { data: activityData } = useQuery({
        queryKey: ['activities', workspace?.id],
        queryFn: () => api.getActivities(workspace!.id, 10),
        enabled: !!workspace?.id,
    });

    const isLoading = okrLoading || boardsLoading;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    const stats = okrData?.stats || {
        total: 0,
        on_track: 0,
        at_risk: 0,
        off_track: 0,
        average_progress: 0,
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold">{t('dashboard.title')}</h1>
                <p className="text-neutral-600 dark:text-neutral-400">
                    {t('dashboard.welcome')}
                </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatsCard
                    title={t('dashboard.overallProgress')}
                    value={`${Math.round(stats.average_progress * 100)}%`}
                    subtitle={`${stats.total} ${t('dashboard.objectives').toLowerCase()}`}
                    icon={TrendingUp}
                    color="primary"
                />
                <StatsCard
                    title={t('dashboard.onTrack')}
                    value={stats.on_track.toString()}
                    subtitle={t('dashboard.objectives').toLowerCase()}
                    icon={CheckCircle2}
                    color="success"
                />
                <StatsCard
                    title={t('dashboard.atRisk')}
                    value={stats.at_risk.toString()}
                    subtitle={t('dashboard.needAttention')}
                    icon={AlertTriangle}
                    color="warning"
                />
                <StatsCard
                    title={t('dashboard.offTrack')}
                    value={stats.off_track.toString()}
                    subtitle={t('dashboard.requireAction')}
                    icon={AlertTriangle}
                    color="danger"
                />
            </div>

            {/* Two column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* OKR Summary */}
                <div className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <Target className="w-5 h-5 text-primary-600" />
                            {t('dashboard.goalsSummary')}
                        </h2>
                        <Link to="/goals" className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">
                            {t('common.viewAll')} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {!okrData?.data?.length ? (
                        <div className="text-center py-8">
                            <Target className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                                {t('dashboard.noObjectives')}
                            </p>
                            <Link to="/goals" className="btn-primary inline-flex">
                                <Plus className="w-4 h-4" />
                                {t('goals.addObjective')}
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {okrData.data.slice(0, 4).map((obj) => (
                                <Link key={obj.id} to="/goals" className="block p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors">
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <span className="text-sm font-medium line-clamp-1">{obj.title}</span>
                                        <StatusBadge status={obj.status} />
                                    </div>
                                    <div className="progress-bar">
                                        <div
                                            className="progress-bar-fill bg-primary-500"
                                            style={{ width: `${Math.round(obj.progress * 100)}%` }}
                                        />
                                    </div>
                                    <div className="flex justify-between mt-1 text-xs text-neutral-500">
                                        <span>{obj.key_results?.length || 0} Key Results</span>
                                        <span>{Math.round(obj.progress * 100)}%</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>

                {/* Boards */}
                <div className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <Kanban className="w-5 h-5 text-secondary-600" />
                            {t('dashboard.recentBoards')}
                        </h2>
                        <button
                            onClick={() => setShowCreateBoard(true)}
                            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                        >
                            <Plus className="w-4 h-4" /> {t('common.add')}
                        </button>
                    </div>

                    {!boardsData?.data?.length ? (
                        <div className="text-center py-8">
                            <Kanban className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                                {t('dashboard.noBoards')}
                            </p>
                            <button
                                onClick={() => setShowCreateBoard(true)}
                                className="btn-primary"
                            >
                                <Plus className="w-4 h-4" />
                                {t('boards.addBoard')}
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            {boardsData.data.slice(0, 3).map((board) => (
                                <Link
                                    key={board.id}
                                    to={`/boards/${board.id}`}
                                    className="p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                                >
                                    <div
                                        className="w-8 h-8 rounded-lg mb-2"
                                        style={{ backgroundColor: board.color || '#06b6d4' }}
                                    />
                                    <p className="text-sm font-medium truncate">{board.name}</p>
                                    <p className="text-xs text-neutral-500">{board.task_count || 0} tasks</p>
                                </Link>
                            ))}

                            <button
                                onClick={() => setShowCreateBoard(true)}
                                className="p-4 border-2 border-dashed border-neutral-200 dark:border-neutral-600 rounded-lg flex flex-col items-center justify-center text-neutral-400 hover:text-primary-600 hover:border-primary-300 cursor-pointer transition-colors"
                            >
                                <Plus className="w-6 h-6 mb-1" />
                                <span className="text-xs">{t('boards.addBoard')}</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Activity Feed - Full width */}
            <ActivityFeed activities={activityData?.activities || []} />

            {/* Create Board Modal */}
            {showCreateBoard && (
                <CreateBoardModal
                    workspaceId={workspace?.id || ''}
                    onClose={() => setShowCreateBoard(false)}
                />
            )}
        </div>
    );
}

function StatsCard({
    title,
    value,
    subtitle,
    icon: Icon,
    color,
}: {
    title: string;
    value: string;
    subtitle: string;
    icon: any;
    color: 'primary' | 'success' | 'warning' | 'danger';
}) {
    const colorClasses = {
        primary: 'bg-primary-50 text-primary-600 dark:bg-primary-600/20',
        success: 'bg-success-50 text-success-600 dark:bg-success-600/20',
        warning: 'bg-warning-50 text-warning-600 dark:bg-warning-600/20',
        danger: 'bg-danger-50 text-danger-600 dark:bg-danger-600/20',
    };

    return (
        <div className="card p-4">
            <div className="flex items-center gap-3">
                <div className={clsx('p-2 rounded-lg', colorClasses[color])}>
                    <Icon className="w-5 h-5" />
                </div>
                <div>
                    <p className="text-2xl font-bold">{value}</p>
                    <p className="text-xs text-neutral-500">{subtitle}</p>
                </div>
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">{title}</p>
        </div>
    );
}

function StatusBadge({ status }: { status: string }) {
    const classes = {
        draft: 'badge-neutral',
        active: 'badge-success',
        completed: 'badge-success',
        at_risk: 'badge-warning',
        off_track: 'badge-danger',
    };

    return (
        <span className={classes[status as keyof typeof classes] || 'badge-neutral'}>
            {status.replace('_', ' ')}
        </span>
    );
}

function CreateBoardModal({
    workspaceId,
    onClose,
}: {
    workspaceId: string;
    onClose: () => void;
}) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        template: 'default',
    });

    const createMutation = useMutation({
        mutationFn: () =>
            api.createBoard({
                workspace_id: workspaceId,
                name: formData.name,
                description: formData.description || undefined,
                template: formData.template,
            }),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['boards'] });
            onClose();
            // Navigate to the new board
            if (data?.data?.id) {
                navigate(`/boards/${data.data.id}`);
            }
        },
    });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-md animate-scale-in">
                <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <h2 className="text-lg font-semibold">Create New Board</h2>
                    <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        createMutation.mutate();
                    }}
                    className="p-4 space-y-4"
                >
                    <div>
                        <label className="label">Board Name</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="input"
                            placeholder="e.g., Sprint 1, Product Backlog"
                            required
                        />
                    </div>

                    <div>
                        <label className="label">Description (optional)</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="input min-h-[60px]"
                            placeholder="What is this board for?"
                        />
                    </div>

                    <div>
                        <label className="label">Template</label>
                        <select
                            value={formData.template}
                            onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                            className="input"
                        >
                            <option value="default">Default (To Do, In Progress, Done)</option>
                            <option value="development">Development (Backlog, To Do, In Progress, Review, Done)</option>
                        </select>
                    </div>

                    {createMutation.isError && (
                        <div className="p-3 bg-danger-50 text-danger-600 text-sm rounded-lg">
                            Failed to create board. Please try again.
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="btn-secondary">
                            Cancel
                        </button>
                        <button type="submit" disabled={createMutation.isPending || !formData.name} className="btn-primary">
                            {createMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <>
                                    <Kanban className="w-4 h-4" />
                                    Create Board
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Activity Feed Widget
function ActivityFeed({ activities }: {
    activities: Array<{
        id: string;
        action: string;
        entity_type: string;
        entity_id: string;
        entity_title: string | null;
        metadata: string;
        created_at: string;
        user_name: string;
        user_avatar: string | null;
    }>
}) {
    const { t } = useTranslation();

    const getActionIcon = (action: string) => {
        if (action === 'created') return <Plus className="w-3 h-3" />;
        if (action === 'completed') return <CheckCircle2 className="w-3 h-3" />;
        if (action === 'assigned') return <User className="w-3 h-3" />;
        if (action === 'invited' || action === 'joined') return <Users className="w-3 h-3" />;
        return <Activity className="w-3 h-3" />;
    };

    const getActionText = (action: string, entityType: string) => {
        const typeLabels: Record<string, string> = {
            objective: 'mục tiêu',
            key_result: 'kết quả then chốt',
            board: 'board',
            task: 'task',
            member: 'thành viên',
            invitation: 'lời mời',
        };
        const actionLabels: Record<string, string> = {
            created: 'đã tạo',
            updated: 'đã cập nhật',
            deleted: 'đã xóa',
            completed: 'đã hoàn thành',
            assigned: 'đã gán',
            commented: 'đã bình luận trên',
            invited: 'đã mời',
            joined: 'đã tham gia',
        };
        return `${actionLabels[action] || action} ${typeLabels[entityType] || entityType}`;
    };

    const formatTimeAgo = (dateStr: string) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'vừa xong';
        if (diffMins < 60) return `${diffMins} phút trước`;
        if (diffHours < 24) return `${diffHours} giờ trước`;
        if (diffDays < 7) return `${diffDays} ngày trước`;
        return date.toLocaleDateString('vi-VN');
    };

    if (activities.length === 0) {
        return (
            <div className="card p-6">
                <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
                    <Activity className="w-5 h-5 text-primary-600" />
                    {t('dashboard.recentActivity', 'Hoạt động gần đây')}
                </h2>
                <div className="text-center py-6 text-neutral-500">
                    <Activity className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">{t('dashboard.noActivities', 'Chưa có hoạt động nào')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="card p-6">
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-primary-600" />
                {t('dashboard.recentActivity', 'Hoạt động gần đây')}
            </h2>
            <div className="space-y-3">
                {activities.slice(0, 8).map((activity) => (
                    <div key={activity.id} className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-neutral-100 dark:bg-neutral-700 rounded-full flex items-center justify-center flex-shrink-0">
                            {activity.user_avatar ? (
                                <img src={activity.user_avatar} alt="" className="w-8 h-8 rounded-full" />
                            ) : (
                                <span className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
                                    {activity.user_name?.charAt(0)?.toUpperCase()}
                                </span>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm">
                                <span className="font-medium">{activity.user_name}</span>{' '}
                                <span className="text-neutral-600 dark:text-neutral-400">
                                    {getActionText(activity.action, activity.entity_type)}
                                </span>
                                {activity.entity_title && (
                                    <span className="font-medium"> "{activity.entity_title}"</span>
                                )}
                            </p>
                            <p className="text-xs text-neutral-400 mt-0.5">
                                {formatTimeAgo(activity.created_at)}
                            </p>
                        </div>
                        <div className={clsx(
                            'p-1.5 rounded-lg',
                            activity.action === 'created' && 'bg-success-50 text-success-600',
                            activity.action === 'completed' && 'bg-primary-50 text-primary-600',
                            activity.action === 'assigned' && 'bg-warning-50 text-warning-600',
                            activity.action === 'joined' && 'bg-secondary-50 text-secondary-600',
                            !['created', 'completed', 'assigned', 'joined'].includes(activity.action) && 'bg-neutral-100 text-neutral-500'
                        )}>
                            {getActionIcon(activity.action)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
